import React, { useEffect, useState } from "react";
import { ShieldAlert, Download } from "lucide-react";
import { api, exportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";
import { Card, Loading, PageHeader, Button, EmptyState } from "@/components/common";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const dateRange = (days) => { const to = new Date(); const from = new Date(); from.setDate(to.getDate() - days + 1); return { date_from: iso(from), date_to: iso(to) }; };

export default function InventoryControlReport() {
  const { branch } = useAuth();
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("product");
  const [data, setData] = useState(null);

  useEffect(() => { if (!branch) return; setData(null); api.get("/inventory/control/report", { branch_id: branch.id, ...dateRange(days) }).then(setData).catch(() => setData({ summary: {}, by_item: [], by_employee: [], waste_by_item: [], top_losses: [] })); }, [branch, days]);

  const rows = tab === "product" ? data?.by_item : tab === "employee" ? data?.by_employee : tab === "waste" ? data?.waste_by_item : data?.top_losses;
  const TABS = [["product", "Per Produk"], ["employee", "Per Karyawan"], ["waste", "Waste/Rusak"], ["top", "Selisih Terbesar"]];

  return (
    <>
      <PageHeader title="Laporan Inventory Control" subtitle="Selisih · waste · pola berulang" icon={ShieldAlert}
        actions={<Button variant="outline" onClick={() => window.open(exportUrl("inventory_control", { branch_id: branch?.id, ...dateRange(days) }), "_blank")} data-testid="control-download"><Download size={16} /> Excel</Button>} />

      {!data ? <Loading /> : (
        <>
          <div className="flex gap-2 mb-4">{[7, 30, 90].map((d) => <button key={d} onClick={() => setDays(d)} data-testid={`control-days-${d}`} className={`px-3 h-9 rounded-full text-sm font-medium ${days === d ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{d} hari</button>)}</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Kpi label="Kerugian selisih" value={rupiah(data.summary?.variance_loss)} color="text-rose-600" testid="control-loss" />
            <Kpi label="Kerugian waste" value={rupiah(data.summary?.waste_loss)} color="text-amber-600" testid="control-waste-loss" />
            <Kpi label="Opname pending" value={num(data.summary?.pending_opname || 0)} color="text-slate-700" testid="control-pending-opname" />
            <Kpi label="Waste pending" value={num(data.summary?.pending_waste || 0)} color="text-amber-600" testid="control-pending-waste" />
          </div>
          <div className="flex flex-wrap gap-2 mb-4">{TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`control-tab-${k}`} className={`px-3 h-9 rounded-full text-sm font-medium ${tab === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}</div>

          {!rows?.length ? <EmptyState icon={ShieldAlert} title="Tidak ada selisih" message="Tidak ada selisih pada periode ini." /> : (
            <div className="space-y-2">
              {rows.map((r, i) => (
                <Card key={r.item_id || r.employee_id || i} className="p-4" data-testid={`control-row-${i}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0"><div className="font-semibold text-slate-800">{r.name}</div><div className="text-xs text-slate-400 mt-0.5">{tab === "waste" ? `${r.count} laporan · ${num(r.qty)} unit` : `${r.sessions || 0} opname · ${r.variance_lines || 0} baris selisih`}</div></div>
                    <span className="text-rose-600 font-bold shrink-0">{rupiah(r.loss_value)}</span>
                  </div>
                  {tab !== "waste" && <div className="flex justify-between text-sm mt-1 text-slate-500"><span>Total selisih absolut</span><span>{num(r.variance_abs)}</span></div>}
                </Card>
              ))}
            </div>
          )}

          {(data.recurring_items?.length || data.recurring_employees?.length) ? (
            <Card className="p-4 mt-4 border border-amber-300" data-testid="control-recurring-patterns">
              <h3 className="text-xs font-bold uppercase tracking-wide text-primary mb-2">Pola Selisih Berulang</h3>
              {data.recurring_items?.map((r) => <KV key={`i-${r.item_id}`} k={r.name} v={`${r.sessions} opname · ${rupiah(r.loss_value)}`} />)}
              {data.recurring_employees?.map((r) => <KV key={`e-${r.employee_id}`} k={r.name} v={`${r.variance_lines} selisih · ${rupiah(r.loss_value)}`} />)}
            </Card>
          ) : null}
        </>
      )}
    </>
  );
}

function Kpi({ label, value, color, testid }) { return (<Card className="p-4" data-testid={testid}><div className="text-xs text-slate-400">{label}</div><div className={`text-xl font-bold mt-1 ${color}`}>{value}</div></Card>); }
const KV = ({ k, v }) => (<div className="flex justify-between text-sm py-0.5"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-700">{v}</span></div>);
